import type {BlockElements} from 'TemplateEditor/Layout/Blocks'
import type {Padding} from 'TemplateEditor/Layout/Blocks/DefaultBlockConfig'
import type {
  Section,
  Template,
  TemplateDefaultPaddingStorage,
} from 'TemplateEditor/template'

type TemplateWithLegacy = Template & {
  globalPadding?: TemplateDefaultPaddingStorage
}

/** Reads `defaultPadding`, or legacy `globalPadding` from older saves. */
export function getDefaultPaddingStorage(
  template: Template,
): TemplateDefaultPaddingStorage | undefined {
  const t = template as TemplateWithLegacy
  return t.defaultPadding ?? t.globalPadding
}

export const zeroPadding = (): Padding => ({
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
})

/** Applies template default paddings to a section just created from the palette. */
export function applyDefaultPaddingToNewSection(
  section: Section,
  template: Template,
): Section {
  const d = getDefaultPaddingStorage(template)
  if (!d) return section

  let next: Section = {...section}

  if (d.section) {
    next = {...next, padding: {...d.section}}
  }

  if (d.column && next.innerStyles) {
    const innerStyles = {...next.innerStyles}
    for (const key of Object.keys(innerStyles)) {
      const k = Number(key)
      if (!Number.isFinite(k)) continue
      const cell = innerStyles[k]
      if (!cell) continue
      innerStyles[k] = {...cell, padding: {...d.column}}
    }
    next = {...next, innerStyles}
  }

  return next
}

/** Applies template default paddings to a block just created from the palette. */
export function applyDefaultPaddingToNewBlock(
  block: BlockElements,
  template: Template,
): BlockElements {
  const d = getDefaultPaddingStorage(template)
  if (!d) return block

  const next = {...block} as BlockElements & {
    padding: Padding
    mobile: {padding: Padding} & Record<string, unknown>
  }

  if (d.block) {
    next.padding = {...d.block}
  }
  const mobilePad = d.blockMobile ?? d.block
  if (mobilePad && next.mobile) {
    next.mobile = {
      ...next.mobile,
      padding: {...mobilePad},
    }
  }
  return next as BlockElements
}
